// components/ContextMenu.js
//
// Right-click menu (no dependencies), styled after Nezo's GridContextMenu so the two apps feel the same.
//
//   const [menu, setMenu] = useState(null);           // { x, y, items }
//   <div onContextMenu={(e) => { e.preventDefault(); setMenu({ x: e.clientX, y: e.clientY, items }); }} />
//   <ContextMenu menu={menu} onClose={() => setMenu(null)} />
//
// items: [{ label, icon?, onClick, disabled?, danger?, dividerAfter? }]
// Closes on Esc, an outside click, scroll or resize. Arrow keys move, Enter / Space run.

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './ContextMenu.css';

function ContextMenu({ menu, onClose }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: 0, top: 0 });
  const items = (menu && menu.items) || [];

  // keep the menu inside the viewport, and put focus on the first item for keyboard use
  useLayoutEffect(() => {
    if (!menu || !ref.current) return;
    const { offsetWidth: w, offsetHeight: h } = ref.current;
    setPos({
      left: Math.max(4, Math.min(menu.x, window.innerWidth - w - 4)),
      top: Math.max(4, Math.min(menu.y, window.innerHeight - h - 4)),
    });
    const first = ref.current.querySelector('button:not(:disabled)');
    if (first) first.focus();
  }, [menu]);

  useEffect(() => {
    if (!menu) return undefined;
    const close = () => onClose();
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('blur', close);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('blur', close);
    };
  }, [menu, onClose]);

  if (!menu || items.length === 0) return null;

  const onKeyDown = (e) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    const buttons = Array.from(ref.current.querySelectorAll('button:not(:disabled)'));
    const i = buttons.indexOf(document.activeElement);
    const next = e.key === 'Home' ? 0
      : e.key === 'End' ? buttons.length - 1
      : e.key === 'ArrowDown' ? (i + 1) % buttons.length
      : (i - 1 + buttons.length) % buttons.length;
    if (buttons[next]) buttons[next].focus();
  };

  return (
    <div
      ref={ref}
      className="cm-menu"
      role="menu"
      style={{ left: pos.left, top: pos.top }}
      onKeyDown={onKeyDown}
      onContextMenu={(e) => e.preventDefault()}
      onClick={(e) => e.stopPropagation()}
    >
      {items.map((item, idx) => (
        <React.Fragment key={`${item.label}-${idx}`}>
          <button
            type="button"
            role="menuitem"
            className={`cm-item ${item.danger ? 'cm-item--danger' : ''}`}
            disabled={item.disabled}
            onClick={(e) => { e.stopPropagation(); onClose(); if (item.onClick) item.onClick(); }}
          >
            <span className="cm-item__icon" aria-hidden="true">{item.icon || ''}</span>
            <span className="cm-item__label">{item.label}</span>
          </button>
          {item.dividerAfter && idx < items.length - 1 && <div className="cm-divider" role="separator" />}
        </React.Fragment>
      ))}
    </div>
  );
}

export default ContextMenu;
